import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon, Menu, X, GraduationCap, Phone, Mail, MapPin, Facebook, Baby, User } from 'lucide-react';
import LoginModal from '@/components/LoginModal';
import axios from 'axios';

const LOGO_URL = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/ukm9lnyj_SANS%20FOND%20V3.png';

const navLinks = [
  { path: '/', label: 'Accueil' },
  { path: '/a-propos', label: 'À propos' },
  { path: '/services', label: 'Services' },
  { path: '/carriere', label: 'Carrière' },
  { path: '/contact', label: 'Contact' },
];

const allNavLinks = [
  { path: '/', label: 'Accueil' },
  { path: '/a-propos', label: 'À propos' },
  { path: '/services', label: 'Services' },
  { path: '/carriere', label: 'Carrière' },
  { path: '/contact', label: 'Contact' },
];

function Navbar() {
  const { isDark, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginRedirect, setLoginRedirect] = useState('/commande');
  const location = useLocation();
  const navigate = useNavigate();

  const isLoggedIn = () => {
    try { return !!JSON.parse(localStorage.getItem('cg-customer')); } catch { return false; }
  };

  const isAdminLoggedIn = () => !!localStorage.getItem('admin_token');

  const handleCommandeClick = (e) => {
    e.preventDefault();
    // Temporaire: redirige vers l'ancien site de commande
    // TODO: Quand le processus de commande sera prêt, revenir à: navigate('/commande') avec modal login
    window.open('https://conceptgourmet.order-online.ai/#/', '_blank');
  };

  const handleConnexionClick = (e) => {
    e.preventDefault();
    if (isLoggedIn()) {
      navigate('/compte');
    } else {
      setLoginRedirect('/compte');
      setShowLogin(true);
    }
  };

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b transition-colors duration-300"
      style={{
        backgroundColor: isDark ? 'rgba(18,22,17,0.85)' : 'rgba(250,249,246,0.85)',
        borderColor: isDark ? 'rgba(44,53,41,0.4)' : 'rgba(216,212,200,0.4)',
      }}
    >
      <div className="w-full px-4">
        <div className="flex items-center h-14">
          <Link to="/" className="flex items-center flex-shrink-0 mr-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Concept Gourmet" className="h-9 w-auto" />
          </Link>

          <nav className="hidden xl:flex items-center flex-1 justify-between" data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                className={`px-2 py-1 text-[clamp(12px,0.85vw,15px)] font-medium transition-colors duration-200 rounded-full whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-[var(--color-primary)] bg-[var(--color-primary)]/10'
                    : 'hover:text-[var(--color-primary)]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={handleCommandeClick}
              data-testid="nav-commande-en-ligne"
              className="flex items-center gap-1.5 px-4 py-2 text-[clamp(11px,0.8vw,14px)] font-bold rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-md"
              style={{ backgroundColor: '#3B82F6', color: '#fff', boxShadow: '0 2px 12px rgba(59,130,246,0.4)' }}
            >
              Commande Particulier / Corpo
            </button>
            <a
              href="https://ecodemo.dev/garderie-login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-commande-garderie"
              className="flex items-center gap-1.5 px-4 py-2 text-[clamp(11px,0.8vw,14px)] font-bold rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-md"
              style={{ backgroundColor: 'var(--color-primary)', color: '#fff', boxShadow: '0 2px 12px rgba(247,138,125,0.4)' }}
            >
              <Baby size={15} />
              Commande Garderie/CPE
            </a>
            <a
              href="https://parent.ecolunch.ca/conceptgourmet"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-commande-scolaire"
              className="flex items-center gap-1.5 px-4 py-2 text-[clamp(11px,0.8vw,14px)] font-bold rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-md"
              style={{ backgroundColor: 'var(--color-secondary)', color: '#fff', boxShadow: '0 2px 12px rgba(109,174,101,0.4)' }}
            >
              <GraduationCap size={15} />
              Commande Scolaire
            </a>
            <div className="relative group">
              <button
                disabled
                data-testid="nav-mon-compte"
                className="flex items-center gap-1.5 px-2 py-1 text-[clamp(12px,0.85vw,15px)] font-medium rounded-full whitespace-nowrap bg-transparent border-0 opacity-40 cursor-not-allowed"
              >
                <User size={14} />
                Mon compte
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-3 py-1.5 rounded-lg text-[11px] font-medium text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                style={{ backgroundColor: '#1A2318' }}>
                Disponible sous peu
              </div>
            </div>

            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="p-1.5 rounded-full transition-all duration-300 hover:scale-110 flex-shrink-0"
              style={{ backgroundColor: isDark ? 'rgba(247,138,125,0.15)' : 'rgba(109,174,101,0.15)' }}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={16} className="text-[#F78A7D]" /> : <Moon size={16} className="text-[#6DAE65]" />}
            </button>
          </nav>

          <div className="flex items-center gap-1 xl:hidden ml-auto">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-mobile"
              className="p-1.5 rounded-full transition-all duration-300"
              style={{ backgroundColor: isDark ? 'rgba(247,138,125,0.15)' : 'rgba(109,174,101,0.15)' }}
            >
              {isDark ? <Sun size={16} className="text-[#F78A7D]" /> : <Moon size={16} className="text-[#6DAE65]" />}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              className="xl:hidden p-2 rounded-lg"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

    </motion.header>

    <AnimatePresence>
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="xl:hidden fixed inset-0 top-14 z-[9999]"
          style={{
            backgroundColor: isDark ? '#121611' : '#FAF9F6',
          }}
          data-testid="mobile-nav"
        >
          <div className="h-full flex flex-col px-6 py-6 overflow-y-auto">
            <div className="space-y-1 flex-1">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-4 py-3.5 rounded-xl text-lg font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-[var(--color-primary)] bg-[var(--color-primary)]/10'
                      : 'hover:text-[var(--color-primary)]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={(e) => { setMobileOpen(false); handleCommandeClick(e); }}
                data-testid="mobile-nav-commande"
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-xl text-lg font-bold text-white bg-transparent border-0 cursor-pointer"
                style={{ backgroundColor: '#3B82F6' }}
              >
                Commande Particulier / Corpo
              </button>
              <div
                className="flex items-center gap-2 w-full text-left px-4 py-3.5 rounded-xl text-lg font-medium opacity-40"
              >
                <User size={20} />
                Mon compte — <span className="text-sm italic">disponible sous peu</span>
              </div>
              <a
                href="https://ecodemo.dev/garderie-login"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-4 py-3.5 rounded-xl text-lg font-semibold"
                style={{ color: 'var(--color-primary)' }}
              >
                <Baby size={20} />
                Commande Garderie / CPE
              </a>
              <a
                href="https://parent.ecolunch.ca/conceptgourmet"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-4 py-3.5 rounded-xl text-lg font-semibold"
                style={{ color: 'var(--color-secondary)' }}
              >
                <GraduationCap size={20} />
                Commande Scolaire
              </a>
            </div>

            <div className="pt-6 mt-4 space-y-3" style={{ borderTop: '1px solid var(--color-border)' }}>
              <a href="tel:4182857251" className="flex items-center gap-3 px-4 text-base" style={{ color: 'var(--color-primary)' }}>
                <Phone size={18} /> 418 285-7251
              </a>
              <a href="mailto:info@conceptgourmet.ca" className="flex items-center gap-3 px-4 text-sm" style={{ color: 'var(--color-muted-fg)' }}>
                <Mail size={16} /> info@conceptgourmet.ca
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    <LoginModal
      isOpen={showLogin}
      onClose={() => setShowLogin(false)}
      redirectTo={loginRedirect}
    />
    </>
  );
}

function Footer() {
  const { isDark } = useTheme();
  const [showAdminCode, setShowAdminCode] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [adminError, setAdminError] = useState('');
  const navigate = useNavigate();

  const handleAdminAccess = async () => {
    setAdminError('');
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, { email: 'admin@conceptgourmet.ca', password: adminCode });
      localStorage.setItem('admin_token', data.token);
      setShowAdminCode(false);
      setAdminCode('');
      navigate('/admin/dashboard');
    } catch {
      setAdminError('Code incorrect');
    }
  };
  return (
    <footer
      data-testid="main-footer"
      className="border-t transition-colors duration-300"
      style={{
        backgroundColor: isDark ? '#0E120D' : '#F0EDE5',
        borderColor: isDark ? '#2C3529' : '#D8D4C8',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <img src={LOGO_URL} alt="Concept Gourmet" className="h-20 w-auto mb-6" />
            <p className="text-sm leading-relaxed" style={{ color: 'var(--color-muted-fg)' }}>
              Service de traiteur dans la région de Portneuf
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Navigation</h4>
            <div className="space-y-2">
              {allNavLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-sm transition-colors hover:text-[var(--color-primary)]"
                  style={{ color: 'var(--color-muted-fg)' }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Coordonnées</h4>
            <div className="space-y-3 text-sm" style={{ color: 'var(--color-muted-fg)' }}>
              <a href="tel:4182857251" className="flex items-center gap-2 hover:text-[var(--color-primary)] transition-colors">
                <Phone size={14} /> 418-285-7251
              </a>
              <a href="mailto:info@conceptgourmet.ca" className="flex items-center gap-2 hover:text-[var(--color-primary)] transition-colors">
                <Mail size={14} /> info@conceptgourmet.ca
              </a>
              <div className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                <span>1335, boulevard Bona-Dussault<br />St-Marc-des-Carrières, Qc, G0A 4B0</span>
              </div>
              <a
                href="https://www.facebook.com/conceptgourmettraiteur"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-[var(--color-primary)] transition-colors mt-2"
              >
                <Facebook size={16} /> Facebook
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4 text-xs" style={{ borderColor: isDark ? '#2C3529' : '#D8D4C8', color: 'var(--color-muted-fg)' }}>
          <span>Concept Gourmet Traiteur &copy; {new Date().getFullYear()} - Tous droits réservés</span>
          <div className="flex items-center gap-3">
            <Link
              to="/termes-et-conditions"
              data-testid="footer-terms-btn"
              className="px-5 py-2 rounded-full text-xs font-medium transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                border: '1px solid var(--color-border)',
              }}
            >
              Termes et conditions
            </Link>
            {!showAdminCode ? (
              <button onClick={() => setShowAdminCode(true)} data-testid="footer-admin-btn"
                className="px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 hover:scale-105 opacity-40 hover:opacity-100"
                style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: '1px solid var(--color-border)' }}>
                Admin
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <input type="password" value={adminCode} onChange={e => { setAdminCode(e.target.value); setAdminError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleAdminAccess()}
                  placeholder="Code" autoFocus data-testid="admin-code-input"
                  className="w-24 px-3 py-1.5 rounded-full text-xs outline-none text-center"
                  style={{ backgroundColor: isDark ? '#121611' : '#fff', border: `1px solid ${adminError ? '#EF4444' : 'var(--color-border)'}`, color: 'var(--color-fg)' }} />
                <button onClick={handleAdminAccess} className="px-3 py-1.5 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: 'var(--color-primary)' }}>OK</button>
                <button onClick={() => { setShowAdminCode(false); setAdminCode(''); setAdminError(''); }} className="text-xs opacity-50 hover:opacity-100">✕</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <>
      <Navbar />
      <main className="pt-14">{children}</main>
      <Footer />
    </>
  );
}
